// ============================================================================
// SESSION GUARD - Intercepts SESSION_EXPIRED errors from edge functions
// When detected, clears the local session and redirects to login.
//
// Edge functions may return errors with HTTP 200 (Supabase SDK puts the body
// in `data` instead of `error`), so detection must inspect both shapes.
// ============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';

const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';

export function extractSessionExpiredCode(value: unknown): string | null {
  try {
    if (value && typeof value === 'object') {
      const e = value as Record<string, unknown>;

      // Direct shape: { error: { code } } — matches result.data on 200 responses
      // where the edge function returned an error body with a successful status.
      const directErr = e.error as Record<string, unknown> | undefined;
      if (directErr && directErr.code === SESSION_EXPIRED_CODE) return SESSION_EXPIRED_CODE;

      // Top-level code (PostgREST-style error on `error`).
      if (e.code === SESSION_EXPIRED_CODE) return SESSION_EXPIRED_CODE;

      // FunctionsHttpError shape: { context: { body: { error: { code } } } }
      if (e.context && typeof e.context === 'object') {
        const ctx = e.context as Record<string, unknown>;
        const body = ctx.body as Record<string, unknown> | undefined;
        const err = (body ?? ctx) as Record<string, unknown>;
        const errObj = err.error as Record<string, unknown> | undefined;
        if (errObj && errObj.code === SESSION_EXPIRED_CODE) return SESSION_EXPIRED_CODE;
      }

      if (typeof e.message === 'string') {
        try {
          const parsed = JSON.parse(e.message);
          if (parsed?.error?.code === SESSION_EXPIRED_CODE) return SESSION_EXPIRED_CODE;
        } catch {}
      }
    }
  } catch {}
  return null;
}

let redirecting = false;

async function forceLogout(client: SupabaseClient) {
  if (redirecting) return;
  redirecting = true;
  try {
    await client.auth.signOut();
  } finally {
    const base = window.location.pathname.startsWith('/admin') ? '/admin' : '';
    window.location.href = `${base}/login?reason=forced_logout`;
  }
}

/**
 * Inspect any value (response data, thrown error, SDK result) for a
 * SESSION_EXPIRED marker and trigger a forced logout if found.
 * Safe to call from any layer (services, fetch wrappers, error handlers).
 * Returns true if a logout was triggered.
 */
export async function handleSessionExpired(
  value: unknown,
  client?: SupabaseClient
): Promise<boolean> {
  if (extractSessionExpiredCode(value) !== SESSION_EXPIRED_CODE) return false;
  await forceLogout(client ?? (await getDefaultClient()));
  return true;
}

let defaultClientRef: SupabaseClient | null = null;

export function setDefaultSessionClient(client: SupabaseClient) {
  defaultClientRef = client;
}

async function getDefaultClient(): Promise<SupabaseClient> {
  if (defaultClientRef) return defaultClientRef;
  // Lazy import to avoid circular dependency at module load time.
  const { supabase } = await import('../services/auth.service');
  defaultClientRef = supabase;
  return supabase;
}

export function patchSessionGuard(client: SupabaseClient) {
  setDefaultSessionClient(client);
  const original = client.functions.invoke.bind(client.functions);

  client.functions.invoke = async (...args: Parameters<typeof original>) => {
    const result = await original(...args);

    // Edge functions can return errors with HTTP 200, in which case the SDK
    // populates `data` instead of `error`. Check both paths.
    const code = extractSessionExpiredCode(result.error) ?? extractSessionExpiredCode(result.data);
    if (code === SESSION_EXPIRED_CODE) {
      await forceLogout(client);
    }

    return result;
  };
}
